import * as styles from "./IntroAppealPageImageWithDonate.module.scss";

import React, { useEffect, useState } from "react";
import CurvedBorderCircular from "../../../components/borders/CurvedBorderCircular";
import CurvedBorderCircularDeep from "../../../components/borders/CurvedBorderCircularDeep";
import CurvedBorderThick from "../../../components/borders/CurvedBorderThick";
import ProcessedImage from "../../../components/common/ProcessedImage/ProcessedImage";
import useWindowWidth from "../../../hooks/useWindowWidth";
import { Colours } from "../../../utils/colours";
import { getSupportWidgetScript } from "../../../utils/scripts";
import cx from 'classnames';


export interface IntroAppealPageImageWithDonateProps {
	title: string;
	text: string;
	imageSrc?: string;
	imageAlt?: string;
	embedCode: string;
	widgetTheme: string;
	isRTE?: boolean;
	imageInHeader?: boolean;
}

export const IntroAppealPageImageWithDonate: React.FC<
	IntroAppealPageImageWithDonateProps
> = ({ title, text, imageSrc, imageAlt, embedCode, widgetTheme, isRTE , imageInHeader}) => {
	const outerRefMobile = React.useRef<HTMLDivElement>(null);
	const innerRefMobile = React.useRef<HTMLDivElement>(null);
	const outerRefDesktop = React.useRef<HTMLDivElement>(null);
	const innerRefDesktop = React.useRef<HTMLDivElement>(null);

	const [hasCalledIframeResizer, setHasCalledIframeResizer] = useState(false);
	useEffect(() => {
		const callIframeResizer = () => {
			if (hasCalledIframeResizer) return;
			if (window.iFrameResize) {
				window.iFrameResize.call();
				setHasCalledIframeResizer(true);
			}
		};
		let interval: NodeJS.Timeout = null;
		if (!hasCalledIframeResizer) {
			interval = setInterval(callIframeResizer, 600);
		} else {
			clearInterval(interval);
		}
		return () => clearInterval(interval);
	}, [hasCalledIframeResizer]);

	useEffect(() => {
		const interval = setInterval(() => {
			if (outerRefMobile.current && innerRefMobile.current) {
				const newHeight = Math.max(
					outerRefMobile.current.clientHeight,
					innerRefMobile.current.clientHeight,
				);
				outerRefMobile.current.style.height = `${newHeight}px`;
			}
			if (outerRefDesktop.current && innerRefDesktop.current) {
				const newHeight = Math.max(
					outerRefDesktop.current.clientHeight,
					innerRefDesktop.current.clientHeight,
				);
				outerRefDesktop.current.style.height = `${newHeight}px`;
			}
		}, 500);
		return () => clearInterval(interval);
	}, []);

	const theme = `widgetouter${widgetTheme || "light"}`;

	const hasImage = !imageInHeader && imageSrc;

	const windowWidth = useWindowWidth();
	if (windowWidth < 1024)
		return (
			<div className={styles.mobile}>
				<div className={styles.herowithimagecontainerouter}>
					{(hasImage) && (
						<div className={styles.herowithimagecontainer}>
								<ProcessedImage
									src={imageSrc}
									dimensions="400x300"
									alt={imageAlt}
									loading="eager"
								/>

							<div className={styles.bordersection}>
								<CurvedBorderCircular
									className={styles.border}
									rotation={0}
									fill={Colours.SUNSHINE_YELLOW}
								/>
							</div>
						</div>
					)}
					<div className={cx(styles.textcontainer, {[styles.textcontainernoimage]: !hasImage})}>
						<h1 className={cx({[styles.title]: !hasImage})}>{title}</h1>
						<div className={cx({[styles.subtext]: !hasImage})}>
							{isRTE ? (
								<div
									// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
									dangerouslySetInnerHTML={{
										__html: text,
									}}
								/>
							) : (
								<p>{text}</p>
							)}
						</div>
					</div>
				</div>
				{embedCode && (
					<div ref={outerRefMobile} className={styles.bordersectionbottom}>
						<CurvedBorderThick
							className={styles.borderbottom}
							fill={Colours.WHITE}
							rotation={0}
						/>
						<div className={styles[theme as keyof typeof styles]}>
							<div ref={innerRefMobile} className={styles.inner}>
								<div
									id="support-script"
									// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
									dangerouslySetInnerHTML={{
										__html: getSupportWidgetScript(embedCode),
									}}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	return (
		<div className={styles.desktop}>
			<div className={styles.herowithimagecontainerouter}>
				<div className={styles.herowithimagecontainerinner}>
					{(hasImage) && (
						<div className={styles.herowithimagecontainer}>
							<ProcessedImage
								src={imageSrc}
								dimensions="600x450"
								alt={imageAlt}
								width={600}
								height={450}
								loading="eager"
							/>

							<div className={styles.bordersection}>
								<CurvedBorderCircularDeep
									className={styles.border}
									rotation={0}
									fill={Colours.SUNSHINE_YELLOW}
								/>
							</div>
						</div>
					)}
					<div className={cx(styles.textcontainer, {[styles.textcontainernoimage]: !hasImage})}>
						<h1 className={cx({[styles.title]: !hasImage})}>{title}</h1>
						<div className={cx({[styles.subtext]: !hasImage})}>
							{isRTE ? (
								<div
									// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
									dangerouslySetInnerHTML={{
										__html: text,
									}}
								/>
							) : (
								<p>{text}</p>
							)}
						</div>
					</div>
				</div>
			</div>
			{embedCode && (
				<div ref={outerRefDesktop} className={styles.bordersectionbottom}>
					<CurvedBorderThick
						className={styles.borderbottom}
						fill={Colours.WHITE}
						rotation={0}
					/>
					<div className={styles[theme as keyof typeof styles]}>
						<div ref={innerRefDesktop} className={styles.inner}>
							<div
								id="support-script"
								// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
								dangerouslySetInnerHTML={{
									__html: getSupportWidgetScript(embedCode),
								}}
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default IntroAppealPageImageWithDonate;
